import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../booking/components/ConsultantCard.css';
import type { HomeTopicCard } from '../homeTopicSections';
import { IllustrativeDisclaimer } from '../../../components/IllustrativeDisclaimer';
import { homeItemOpensPage } from '../utils/homeItemClick';
import { useClientCountry } from '../../../hooks/useClientCountry';

const BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  demand: { bg: '#fff1f1', color: '#FF0100' },
  free: { bg: '#ECFDF5', color: '#059669' },
  new: { bg: '#EFF6FF', color: '#1D4ED8' },
};

interface TopicServiceCardProps {
  item: HomeTopicCard;
  index: number;
  onSelect: (categoryId: string, slug: string) => void;
}

export const TopicServiceCard: React.FC<TopicServiceCardProps> = ({ item, index, onSelect }) => {
  const [open, setOpen] = useState(false);
  const { country } = useClientCountry();
  const badge = item.badgeType ? BADGE_STYLES[item.badgeType] : null;
  const opensPage = homeItemOpensPage(item, country);

  const handleActivate = () => {
    if (opensPage) {
      onSelect(item.categoryId, item.slug);
      return;
    }
    setOpen(true);
  };

  const handleConnect = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpen(false);
    onSelect(item.categoryId, item.slug);
  };

  return (
    <>
      <motion.div
        className="ccard"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-40px' }}
        transition={{ duration: 0.45, delay: index * 0.09, ease: [0.4, 0, 0.2, 1] }}
        whileHover={{ y: -5, transition: { duration: 0.2 } }}
        onClick={handleActivate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleActivate()}
        aria-label={opensPage ? `Open ${item.title}` : `View details for ${item.title}`}
      >
        <div className="ccard-thumb">
          <img src={item.image} alt={item.title} className="ccard-img" />
          {badge && item.badge && (
            <span className="ccard-badge" style={{ background: badge.bg, color: badge.color }}>
              {item.badge}
            </span>
          )}
          <div className="ccard-thumb-overlay">
            <span className="ccard-thumb-hint">
              <i className="ti ti-eye" style={{ fontSize: 14 }} /> Quick view
            </span>
          </div>
        </div>

        <div className="ccard-body">
          <h3 className="ccard-name">{item.title}</h3>
          <p className="ccard-info">{item.shortDesc}</p>
        </div>

        <div className="ccard-foot">
          <span className="ccard-fee">{item.meta ?? 'Available now'}</span>
          <span className="ccard-cta">
            {opensPage ? 'Browse' : 'Connect'} <i className="ti ti-arrow-right" style={{ fontSize: 12 }} />
          </span>
        </div>
      </motion.div>

      <AnimatePresence>
        {open && (
                      <>
            <motion.div
              className="svc-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.22 }}
              onClick={() => setOpen(false)}
            />

            <div className="svc-modal-wrapper">
              <motion.div
                className="svc-modal"
                role="dialog"
                aria-modal="true"
                aria-label={item.title}
                initial={{ opacity: 0, scale: 0.93, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 20 }}
                transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
              >
                <button className="svc-modal-close" onClick={() => setOpen(false)} aria-label="Close">
                  <i className="ti ti-x" />
                </button>

                <div className="svc-modal-img-wrap" style={{ position: 'relative' }}>
                  <img src={item.image} alt={item.title} className="svc-modal-img" />
                  {badge && item.badge && (
                    <span className="svc-modal-badge" style={{ background: badge.bg, color: badge.color }}>
                      {item.badge}
                    </span>
                  )}
                  <IllustrativeDisclaimer className="illus-disclaimer--modal" />
                </div>

                <div className="svc-modal-body">
                  <motion.h2
                    className="svc-modal-title"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.14, duration: 0.38 }}
                  >
                    {item.title}
                  </motion.h2>

                  <motion.p
                    className="svc-modal-desc"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.38 }}
                  >
                    {item.shortDesc}
                  </motion.p>

                  <motion.div
                    className="svc-modal-actions"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.26, duration: 0.38 }}
                  >
                    <button type="button" className="svc-modal-btn" onClick={handleConnect}>
                      Connect now <i className="ti ti-message-circle" style={{ fontSize: 14 }} />
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            </div>
            </>
                  )}
      </AnimatePresence>
    </>
  );
};
